// ParameterField.js
// One run parameter, from its label down to its input. Both run-parameter rails
// render this and nothing else: the Function pane's rail on the Dashboard, and
// the rail inside a View.
//
// WHICH control a parameter gets is a property of the COLUMN, never of the rail
// it happens to be drawn in: a range filter is a From/To date pair, a
// tag-category column is a searchable multi-select over its known values, a
// Yes/No column is a two-option select, an indexed text column is a
// server-searched autocomplete, a Start/End Date column is a single date
// picker, and everything else is a comma-separated text box. Each rail used to
// carry its own copy of that ladder, which is how the same column came to offer
// different controls depending on where you looked.
//
// The VALUE SHAPES emitted through `onChange` differ per control, deliberately,
// because they are the shapes the run builders already parse:
//     range filter                -> { gte, lte } of 'YYYY-MM-DD'
//     multi-select / autocomplete -> array of values
//     date picker                 -> 'YYYY-MM-DD', or '' once cleared
//     text box                    -> the raw string, parsed at run time
// A caller that stores anything else here will send anything else to the run.

import React, { useEffect, useState } from 'react';
import {
  Box,
  Checkbox,
  Chip,
  FormControl,
  ListItemText,
  ListSubheader,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import AppDatePicker from './AppDatePicker';
import {
  formatParameterInputValue,
  parameterLabel,
  getParameterOptionKey,
  getParameterValueLabel,
  isRangeParamValue,
} from '../../utils/functionParameterUtils';
import { useIndexedValueSearch } from '../../hooks/useIndexedValueSearch';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

// The IS/NOT chip flips a filter between "keep only these values" and "keep
// everything except these". Two letters carry that on their own only once you
// already know the control, so the meaning and the effect of clicking are both
// stated for the pointer and for a screen reader.
function ExcludeChip({ excluded, onToggle, disabled = false }) {
  const description = disabled
    ? (excluded ? 'Excluding matching values' : 'Including matching values')
    : (excluded ? 'Excluding matching values — click to include' : 'Include matching values');
  return (
    <Tooltip title={description}>
      <Chip
        label={excluded ? 'NOT' : 'IS'}
        size="small"
        color={excluded ? 'error' : 'default'}
        variant={excluded ? 'filled' : 'outlined'}
        onClick={disabled ? undefined : onToggle}
        disabled={disabled}
        aria-label={description}
        sx={{ ml: 1, cursor: disabled ? 'default' : 'pointer', minWidth: 42 }}
      />
    </Tooltip>
  );
}

// A text column the table indexes is searched on the server as the user types:
// the value list is far too large to ship to the browser up front.
function IndexedValueAutocomplete({ tableIds, columnName, values, disabled, onChange }) {
  const [inputValue, setInputValue] = useState('');
  const { search, options, loading } = useIndexedValueSearch(tableIds, columnName);

  // Always called: an emptied input clears the options and invalidates a read
  // still in flight, and a new search callback (table or column change)
  // re-runs the current term against the new scope.
  useEffect(() => {
    search(inputValue);
  }, [inputValue, search]);

  const optionLabels = (options || []).map(option => option?.value).filter(value => typeof value === 'string');
  const combinedOptions = Array.from(new Set([...(values || []), ...optionLabels]));

  return (
    <Autocomplete
      multiple
      freeSolo
      disabled={disabled}
      options={combinedOptions}
      value={values || []}
      renderTags={(selected, getTagProps) =>
        (selected || []).map((option, index) => {
          const label = typeof option === 'string' ? option : option?.value;
          return (
            <Tooltip key={`${label}-${index}`} title={label} arrow>
              <span>
                <Chip label={label} {...getTagProps({ index })} />
              </span>
            </Tooltip>
          );
        })
      }
      onChange={(_, newValues) => {
        const cleaned = (newValues || [])
          .map(value => (typeof value === 'string' ? value : value?.value))
          .filter(value => typeof value === 'string' && value.trim() !== '');
        onChange(cleaned);
      }}
      inputValue={inputValue}
      onInputChange={(_, newInput) => setInputValue(newInput)}
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth
          size="small"
          placeholder={loading ? 'Searching...' : ''}
        />
      )}
    />
  );
}

function ParameterField({
  name,
  // Shown instead of `name` when the control needs to say which source it
  // belongs to; `name` stays the column for lookups.
  label = null,
  value,
  onChange,
  type = 'Text',
  // A range filter (From/To), as opposed to a start_date/end_date pair of
  // separate single-date parameters.
  isRange = false,
  isDateRange = false,
  isAttribute = false,
  // No default: for an attribute parameter, `undefined` means the category's
  // values have not arrived yet and renders as loading (see below); [] means
  // the category has no values. Every other use guards with `|| []`.
  availableValues,
  indexedTableIds = [],
  disabled = false,
  error = '',
  labelVariant = 'body2',
  excluded = false,
  // Omitted where a rail does not support excluding at all; the chip is then
  // never drawn.
  onToggleExclude = null,
}) {
  // The option filter is ephemeral: it belongs to this open menu and nothing
  // above needs to remember it.
  const [searchTerm, setSearchTerm] = useState('');

  const selectedArray = Array.isArray(value) ? value : [];
  const rangeValue = isRange && isRangeParamValue(value) ? value : { gte: '', lte: '' };
  const textValue = isRange
    ? ''
    : formatParameterInputValue(value, type, { multi: !isDateRange, dateRange: isDateRange });

  const filteredValues = (availableValues || []).filter((option) => (
    (option?.name || '').toLowerCase().includes(searchTerm.toLowerCase())
  ));

  // Excluding is set membership, so it is meaningless on a single date and on a
  // range, and pointless on a filter the version fixed rather than exposed. A
  // read-only control still shows that it excludes — a NOT the reader cannot
  // see is a filter they will misread.
  const showExcludeChip = Boolean(onToggleExclude) && !isDateRange && !isRange && (!disabled || excluded);

  let control;
  if (isRange) {
    control = (
      <Stack spacing={1}>
        <AppDatePicker
          label="From"
          fullWidth
          value={rangeValue.gte}
          onChange={(nextDate, context) => onChange({ ...rangeValue, gte: nextDate }, context)}
          disabled={disabled}
        />
        <AppDatePicker
          label="To"
          fullWidth
          value={rangeValue.lte}
          onChange={(nextDate, context) => onChange({ ...rangeValue, lte: nextDate }, context)}
          disabled={disabled}
        />
      </Stack>
    );
  } else if (isAttribute && availableValues == null) {
    // The category's values have not arrived yet. This is shown as the picker
    // it will become, not as the free-text fallback below: a value typed into
    // that fallback would be stored as a string and sent with the run.
    control = (
      <FormControl fullWidth size="small">
        <Select multiple value={[]} disabled displayEmpty renderValue={() => 'Loading values…'} />
      </FormControl>
    );
  } else if (isAttribute && (availableValues || []).length > 0) {
    control = (
      <FormControl fullWidth size="small">
        <Select
          multiple
          value={selectedArray}
          onChange={(e) => onChange(Array.isArray(e.target.value) ? e.target.value : [])}
          onClose={() => setSearchTerm('')}
          disabled={disabled}
          renderValue={(selected) => (
            Array.isArray(selected)
              ? selected.map((item) => getParameterValueLabel(item, type, availableValues)).join(', ')
              : ''
          )}
          MenuProps={MenuProps}
        >
          {/* The search box lives inside the menu, so its own mouse and key
              events must not reach the Select's option handling. */}
          <div onMouseDown={(e) => e.stopPropagation()}>
            <ListSubheader>
              <TextField
                size="small"
                autoFocus
                placeholder="Search..."
                fullWidth
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key !== 'Escape') e.stopPropagation();
                }}
              />
            </ListSubheader>
          </div>
          {filteredValues.map((option) => (
            <MenuItem key={getParameterOptionKey(option.value)} value={option.value}>
              <Checkbox checked={selectedArray.includes(option.value)} />
              <ListItemText primary={option.name} />
            </MenuItem>
          ))}
          {filteredValues.length === 0 && searchTerm && (
            <MenuItem disabled>No matching options</MenuItem>
          )}
        </Select>
      </FormControl>
    );
  } else if (type === 'Yes/No') {
    control = (
      <FormControl fullWidth size="small">
        <Select
          multiple
          value={selectedArray}
          onChange={(e) => onChange(Array.isArray(e.target.value) ? e.target.value : [])}
          disabled={disabled}
          renderValue={(selected) => (
            Array.isArray(selected)
              ? selected.map((item) => getParameterValueLabel(item, type)).join(', ')
              : ''
          )}
        >
          <MenuItem value={true}>Yes</MenuItem>
          <MenuItem value={false}>No</MenuItem>
        </Select>
      </FormControl>
    );
  } else if ((indexedTableIds || []).length > 0) {
    control = (
      <IndexedValueAutocomplete
        tableIds={indexedTableIds}
        columnName={name}
        values={selectedArray}
        disabled={disabled}
        onChange={(values) => onChange(values || [])}
      />
    );
  } else if (isDateRange) {
    control = (
      <AppDatePicker
        fullWidth
        value={textValue}
        onChange={onChange}
        disabled={disabled}
      />
    );
  } else {
    control = (
      <TextField
        fullWidth
        size="small"
        value={textValue}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        error={Boolean(error)}
        inputProps={{ 'aria-label': label || name }}
        placeholder={
          type === 'Number'
            ? 'Comma-separated numbers'
            : type === 'Date'
              ? 'YYYY-MM-DD, YYYY-MM-DD'
              : undefined
        }
      />
    );
  }

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
        <Typography variant={labelVariant} noWrap sx={{ minWidth: 0 }}>
          {parameterLabel(label || name)}
        </Typography>
        {showExcludeChip && <ExcludeChip excluded={excluded} onToggle={onToggleExclude} disabled={disabled} />}
      </Box>
      {control}
      {error && <Typography role="alert" variant="caption" color="error">{error}</Typography>}
    </Box>
  );
}

export default ParameterField;
